import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Download,
  PencilLine,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react'
import {
  fetchComponentMaster,
  fetchDeviceColors,
  fetchDevices,
  fetchMachines,
  updateDeviceColors,
  updateMachineColors,
} from '@/services/api'
import type { Device } from '@/types/device'
import {
  COMPONENT_TEMPLATE,
  SOFTWARE_VERSION_KEYS,
  applyColorPatch,
  type Machine,
  type MachineColorPatch,
  type MachineColors,
} from '@/types/machine'
import MachineTable, {
  ColumnPicker,
  PaintButton,
  allColumnKeys,
  loadVisibleColumns,
  machinesToCsv,
  saveVisibleColumns,
  sortMachineRows,
  toggleVisibleColumn,
  type MachineRow,
  type MachineTableHandle,
  type PaintAction,
  type PaintUpdate,
} from '@/components/MachineTable'
import RegisterMachineDialog from '@/components/RegisterMachineDialog'
import MachineEditGrid from '@/components/MachineEditGrid'

export default function MachinesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // ON にすると削除済み（deletedAt あり）の機材のみを表示する
  const [showDeleted, setShowDeleted] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [registerOpen, setRegisterOpen] = useState(false)
  const [registerPrefill, setRegisterPrefill] = useState<{
    serialNumber?: string
    deviceName?: string
  } | null>(null)
  // 表示列は部品マスタ取得後に初期化する（部品列がマスタ駆動のため）
  const [visibleColumns, setVisibleColumns] = useState<string[] | null>(null)
  const [editMode, setEditMode] = useState(false)
  // 一覧のソート状態。一括編集・CSVも同じ並び順にするためここで持つ
  const [sort, setSort] = useState({ key: 'serial', asc: true })

  // ツールバーの塗りつぶしボタン用: テーブル内の選択状態への参照と、
  // 色を付けられる選択セル数（活性制御）、前回適用した色（再適用）
  const tableRef = useRef<MachineTableHandle>(null)
  const [paintableCount, setPaintableCount] = useState(0)
  const [lastPaint, setLastPaint] = useState<PaintAction | null>(null)

  const machinesQuery = useQuery({
    queryKey: ['machines'],
    queryFn: fetchMachines,
  })
  const devicesQuery = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
  })
  const componentsQuery = useQuery({
    queryKey: ['componentMaster'],
    queryFn: fetchComponentMaster,
  })
  // 台帳未登録の IoT デバイス行のマーカー色（取得失敗時は色なしで表示を継続）
  const deviceColorsQuery = useQuery({
    queryKey: ['deviceColors'],
    queryFn: fetchDeviceColors,
  })
  // マスタ取得に失敗しても既定テンプレートで一覧表示は継続する
  const componentDefs =
    componentsQuery.data ??
    (componentsQuery.isError ? COMPONENT_TEMPLATE : undefined)
  const columnKeys = useMemo(
    () => (componentDefs ? allColumnKeys(componentDefs) : null),
    [componentDefs]
  )

  useEffect(() => {
    if (columnKeys && visibleColumns === null) {
      setVisibleColumns(loadVisibleColumns(columnKeys))
    }
  }, [columnKeys, visibleColumns])

  // マーカー色の変更（選択範囲の機材・未登録デバイス分をまとめて）。
  // 楽観的にキャッシュへ反映し、失敗時は元へ戻す
  const paintMutation = useMutation({
    mutationFn: (
      updates: {
        machineId?: string
        deviceName?: string
        patch: MachineColorPatch
      }[]
    ) =>
      Promise.all(
        updates.map((u) =>
          u.machineId
            ? updateMachineColors(u.machineId, u.patch)
            : updateDeviceColors(u.deviceName!, u.patch)
        )
      ),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ['machines'] })
      await queryClient.cancelQueries({ queryKey: ['deviceColors'] })
      const previous = queryClient.getQueryData<Machine[]>(['machines'])
      const previousDeviceColors = queryClient.getQueryData<
        Record<string, MachineColors>
      >(['deviceColors'])
      const patchById = new Map(
        updates.filter((u) => u.machineId).map((u) => [u.machineId, u.patch])
      )
      queryClient.setQueryData<Machine[]>(['machines'], (machines) =>
        (machines ?? []).map((m) => {
          const patch = patchById.get(m.machineId)
          return patch ? applyColorPatch(m, patch) : m
        })
      )
      queryClient.setQueryData<Record<string, MachineColors>>(
        ['deviceColors'],
        (map) => {
          const next = { ...(map ?? {}) }
          for (const u of updates) {
            if (!u.deviceName) continue
            next[u.deviceName] = applyColorPatch(
              next[u.deviceName] ?? {},
              u.patch
            )
          }
          return next
        }
      )
      return { previous, previousDeviceColors }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['machines'], context.previous)
      }
      if (context?.previousDeviceColors) {
        queryClient.setQueryData(['deviceColors'], context.previousDeviceColors)
      }
      // 一部だけ成功した可能性があるためサーバーの状態へ合わせ直す
      queryClient.invalidateQueries({ queryKey: ['machines'] })
      queryClient.invalidateQueries({ queryKey: ['deviceColors'] })
    },
    // サーバー確定値で置き換える（他ユーザーの変更とのマージ結果を反映）
    onSuccess: (results) => {
      const machineById = new Map<string, Machine>()
      const colorsByDevice = new Map<string, MachineColors>()
      for (const result of results) {
        if ('machineId' in result) {
          machineById.set(result.machineId, result)
        } else {
          const { deviceName, ...colors } = result
          colorsByDevice.set(deviceName, colors)
        }
      }
      queryClient.setQueryData<Machine[]>(['machines'], (list) =>
        (list ?? []).map((m) => machineById.get(m.machineId) ?? m)
      )
      queryClient.setQueryData<Record<string, MachineColors>>(
        ['deviceColors'],
        (map) => {
          const next = { ...(map ?? {}) }
          for (const [deviceName, colors] of colorsByDevice) {
            next[deviceName] = colors
          }
          return next
        }
      )
    },
  })

  const handlePaint = (updates: PaintUpdate[], action: PaintAction) => {
    // 塗りつぶしボタンが「前回の色」としてワンクリック再適用できるよう覚える
    setLastPaint(action)
    if (updates.length === 0) return
    paintMutation.mutate(
      updates.map((u) => ({
        machineId: u.machine?.machineId,
        deviceName: u.deviceName,
        patch: u.patch,
      }))
    )
  }

  const deviceByName = useMemo(() => {
    const map = new Map<string, Device>()
    for (const device of devicesQuery.data ?? []) {
      map.set(device.deviceName, device)
    }
    return map
  }, [devicesQuery.data])

  const rows = useMemo<MachineRow[]>(() => {
    const machines = machinesQuery.data ?? []
    const referenced = new Set(
      machines.map((m) => m.deviceName).filter(Boolean)
    )
    const machineRows = machines.map((machine) => ({
      key: machine.machineId,
      machine,
      device: machine.deviceName
        ? (deviceByName.get(machine.deviceName) ?? null)
        : null,
    }))
    // 台帳未登録の IoT デバイス（フォークリフトのみ。PLC・カメラは対象外）
    const unregistered = (devicesQuery.data ?? [])
      .filter(
        (d) =>
          (d.deviceType === 'forklift' || d.deviceType === null) &&
          !referenced.has(d.deviceName)
      )
      .map((device) => ({
        key: `iot:${device.deviceName}`,
        machine: null,
        device,
        deviceColors: deviceColorsQuery.data?.[device.deviceName] ?? null,
      }))
    return [...machineRows, ...unregistered]
  }, [
    machinesQuery.data,
    devicesQuery.data,
    deviceByName,
    deviceColorsQuery.data,
  ])

  const filteredRows = useMemo(() => {
    // 通常は未削除のみ、「削除済みを表示」ON のときは削除済みのみ
    let result = showDeleted
      ? rows.filter((row) => row.machine?.deletedAt)
      : rows.filter((row) => !row.machine?.deletedAt)
    const query = searchText.trim().toLowerCase()
    if (query) {
      // 全項目（構成部品・ソフトバージョン含む）に対する部分一致
      result = result.filter((row) => {
        const m = row.machine
        const haystack = [
          m?.serialNumber,
          m?.deviceName,
          m?.customer,
          m?.edition,
          m?.lot,
          m?.bodySerial,
          m?.shippedAt,
          m?.supportStatus,
          m?.bodyRecord,
          m?.bodyInspectionReport,
          m?.remarks,
          ...SOFTWARE_VERSION_KEYS.map((key) => m?.softwareVersions?.[key]),
          ...(m?.components ?? []).flatMap((c) => [
            c.name,
            c.serialNumber,
            c.note,
          ]),
          row.device?.deviceName,
          row.device?.siteId,
          row.device?.certificates[0]?.status,
        ]
          .filter(Boolean)
          .join('\n')
          .toLowerCase()
        return haystack.includes(query)
      })
    }
    return result
  }, [rows, showDeleted, searchText])

  // 検索・削除フィルタ適用後の行を、一覧と同じ並び順の全列CSVでダウンロード
  const handleDownloadCsv = () => {
    if (!componentDefs) return
    const csv = machinesToCsv(
      t,
      sortMachineRows(filteredRows, componentDefs, sort.key, sort.asc),
      componentDefs
    )
    // Excel で文字化けしないよう BOM 付き UTF-8 にする
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `machines_${new Date().toISOString().slice(0, 10)}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const handleSelectRow = (row: MachineRow) => {
    if (row.machine) {
      navigate(`/machines/${row.machine.machineId}`)
    } else if (row.device) {
      // 台帳未登録の IoT デバイス → 台帳へ取り込み
      const name = row.device.deviceName
      setRegisterPrefill({
        serialNumber: /^fork_20\d{6}$/.test(name) ? name.slice(7) : name,
        deviceName: name,
      })
      setRegisterOpen(true)
    }
  }

  if (
    machinesQuery.isLoading ||
    devicesQuery.isLoading ||
    !componentDefs ||
    !columnKeys ||
    !visibleColumns
  ) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="relative flex-1 min-w-[12rem] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('machines.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowDeleted((v) => !v)}
            title={t('machines.showDeletedHint')}
            className={`flex items-center px-3 py-2 text-sm border rounded-md transition-colors ${
              showDeleted
                ? 'text-red-700 bg-red-50 border-red-300'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              machinesQuery.refetch()
              devicesQuery.refetch()
            }}
            disabled={machinesQuery.isFetching || devicesQuery.isFetching}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            title={t('common.refresh')}
          >
            <RefreshCw
              className={`h-4 w-4 ${
                machinesQuery.isFetching || devicesQuery.isFetching
                  ? 'animate-spin'
                  : ''
              }`}
            />
          </button>
          <button
            onClick={handleDownloadCsv}
            disabled={filteredRows.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
            title={t('machines.downloadCsv')}
          >
            <Download className="h-4 w-4" />
          </button>
          <PaintButton
            disabled={editMode || paintableCount === 0}
            lastPaint={lastPaint}
            onPick={(type, color) =>
              tableRef.current?.paintSelection(type, color)
            }
          />
          <ColumnPicker
            componentDefs={componentDefs}
            visible={visibleColumns}
            onToggle={(key) =>
              setVisibleColumns((prev) =>
                toggleVisibleColumn(prev ?? [], key, columnKeys)
              )
            }
            onSet={(keys) =>
              setVisibleColumns(saveVisibleColumns(keys, columnKeys))
            }
          />
          <button
            onClick={() => {
              setEditMode(true)
              // 一括編集中はテーブルが消えるため選択も無効にする
              setPaintableCount(0)
            }}
            disabled={editMode}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors whitespace-nowrap"
          >
            <PencilLine className="h-4 w-4" />
            {t('machines.bulkEdit')}
          </button>
          <button
            onClick={() => {
              setRegisterPrefill(null)
              setRegisterOpen(true)
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            {t('machines.register')}
          </button>
        </div>
      </div>

      {/* Error */}
      {(machinesQuery.isError || devicesQuery.isError) && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {t('machines.fetchError')}
        </div>
      )}
      {paintMutation.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {t('machines.colorError')}
        </div>
      )}

      {/* Table / 一括編集グリッド */}
      {editMode ? (
        <MachineEditGrid
          // 現在のフィルタに一致する台帳登録済み機材のみ、一覧と同じ並び順で編集対象
          machines={sortMachineRows(
            filteredRows,
            componentDefs,
            sort.key,
            sort.asc
          )
            .map((row) => row.machine)
            .filter((m): m is NonNullable<typeof m> => m !== null)}
          componentDefs={componentDefs}
          onClose={() => setEditMode(false)}
          onSaved={() => {
            setEditMode(false)
            queryClient.invalidateQueries({ queryKey: ['machines'] })
          }}
        />
      ) : (
        // アプリヘッダ・ページ余白・ツールバー分を除いた高さに収め、表の中で縦横スクロールさせる
        // isolate: sticky ヘッダの z-index が表の外（列ピッカー等）と競合しないよう閉じ込める
        <div className="isolate bg-white rounded-lg border border-gray-200 shadow-sm overflow-auto max-h-[calc(100vh-11.875rem)]">
          <MachineTable
            ref={tableRef}
            rows={filteredRows}
            componentDefs={componentDefs}
            visibleColumns={visibleColumns}
            highlight={searchText}
            sortKey={sort.key}
            sortAsc={sort.asc}
            onSortChange={(key, asc) => setSort({ key, asc })}
            onSelect={handleSelectRow}
            onPaint={handlePaint}
            onSelectionChange={setPaintableCount}
          />
        </div>
      )}

      {/* Dialogs */}
      <RegisterMachineDialog
        open={registerOpen}
        machines={machinesQuery.data ?? []}
        componentDefs={componentDefs}
        prefill={registerPrefill}
        onClose={() => setRegisterOpen(false)}
        onCreated={(machine) => {
          setRegisterOpen(false)
          queryClient.invalidateQueries({ queryKey: ['machines'] })
          // 取り込んだデバイスの色は機材側へ引き継がれ、デバイス色からは消える
          queryClient.invalidateQueries({ queryKey: ['deviceColors'] })
          navigate(`/machines/${machine.machineId}`)
        }}
      />
    </div>
  )
}
